import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getLanguage, t, useStoreLanguage } from "../../../features/store-language/i18n.ts";

interface SeoDefinition {
  title: string;
  description: string;
  robots: string;
  structuredType?: "CollectionPage";
}

function categoryName(pathname: string): string {
  const slug = pathname.replace(/^\/categories\//, "");
  try {
    return decodeURIComponent(slug).replace(/[-_]+/g, " ").trim();
  } catch {
    return slug.replace(/[-_]+/g, " ").trim();
  }
}

function getSeoDefinition(pathname: string): SeoDefinition | null {
  if (pathname === "/categories") {
    return {
      title: `${t("دسته‌بندی محصولات")} | ${t("کاتالوگ رَج")}`,
      description: t("پیدا کردن طرح دلخواه شما"),
      robots: "index,follow,max-image-preview:large,max-snippet:-1",
      structuredType: "CollectionPage",
    };
  }
  if (pathname.startsWith("/categories/")) {
    const name = categoryName(pathname);
    return {
      title: `${name} | ${t("کاتالوگ رَج")}`,
      description: `${t("رنگ‌ها و نقش‌ها را کشف کنید")} — ${name}`,
      robots: "index,follow,max-image-preview:large,max-snippet:-1",
      structuredType: "CollectionPage",
    };
  }
  if (pathname === "/" || pathname === "/products") {
    return {
      title: `${t("همهٔ محصولات")} | ${t("کاتالوگ رَج")}`,
      description: t("رنگ‌ها و نقش‌ها را کشف کنید"),
      robots: "index,follow,max-image-preview:large,max-snippet:-1",
      structuredType: "CollectionPage",
    };
  }
  if (pathname === "/search") {
    return {
      title: `${t("ویترین طرح‌ها")} | ${t("کاتالوگ رَج")}`,
      description: t("رنگ‌ها و نقش‌ها را کشف کنید"),
      robots: "noindex,follow",
    };
  }
  if (/^\/account(\/|$)/.test(pathname)) {
    return {
      title: `${t("حساب من")} | ${t("کاتالوگ رَج")}`,
      description: t("دسترسی سریع پورتال"),
      robots: "noindex,nofollow,noarchive",
    };
  }
  if (/^\/(schema|schemaId)(\/|$)/.test(pathname)) {
    return {
      title: t("کاتالوگ رَج"),
      description: t("رنگ‌ها و نقش‌ها را کشف کنید"),
      robots: "noindex,follow",
    };
  }
  return null;
}

export default function StorefrontSeo() {
  const storeLanguage = useStoreLanguage();
  const { pathname } = useLocation();

  useEffect(function () {
    void storeLanguage;
    if (/^\/products\/[^/]+$/.test(pathname)) return undefined;
    const definition = getSeoDefinition(pathname);
    if (!definition) return undefined;

    const canonicalPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
    const canonicalUrl = new URL(canonicalPath, window.location.origin).toString();
    const previousTitle = document.title;

    function setMeta(attribute: "name" | "property", key: string, content: string) {
      let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
      const previousContent = element?.getAttribute("content");
      const created = !element;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
      return function restoreMeta() {
        if (created) element?.remove();
        else if (previousContent == null) element?.removeAttribute("content");
        else element?.setAttribute("content", previousContent);
      };
    }

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const previousCanonical = canonical?.getAttribute("href");
    const createdCanonical = !canonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }

    document.title = definition.title;
    canonical.href = canonicalUrl;
    const restoreMeta = [
      setMeta("name", "description", definition.description),
      setMeta("name", "robots", definition.robots),
      setMeta("name", "googlebot", definition.robots),
      setMeta("property", "og:type", "website"),
      setMeta("property", "og:site_name", t("کاتالوگ رَج")),
      setMeta("property", "og:title", definition.title),
      setMeta("property", "og:description", definition.description),
      setMeta("property", "og:url", canonicalUrl),
      setMeta("name", "twitter:card", "summary"),
      setMeta("name", "twitter:title", definition.title),
      setMeta("name", "twitter:description", definition.description),
    ];

    let structuredData: HTMLScriptElement | null = null;
    if (definition.structuredType) {
      structuredData = document.createElement("script");
      structuredData.type = "application/ld+json";
      structuredData.dataset.rajStorefrontSeo = "true";
      structuredData.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": definition.structuredType,
        name: definition.title,
        description: definition.description,
        url: canonicalUrl,
        inLanguage: getLanguage(),
      });
      document.head.appendChild(structuredData);
    }

    return function cleanupSeoMetadata() {
      document.title = previousTitle;
      restoreMeta.forEach(function (restore) { restore(); });
      structuredData?.remove();
      if (createdCanonical) canonical?.remove();
      else if (previousCanonical == null) canonical?.removeAttribute("href");
      else canonical?.setAttribute("href", previousCanonical);
    };
  }, [pathname, storeLanguage]);

  return null;
}
